import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { InvitationStatus } from '~/api/invitations/types';

import { useUser } from '~/api/user';
import { Title, useTable } from '~/components';
import { ColumnIds } from '~/constants/table';

import { wrapperStyle } from './SentInvitationsTable.css';
import { useSentInvitationsTableColumns } from './SentInvitationsTable.hooks';

const invitationSortPriority = { accepted: 1, pending: 2, declined: 3 } as const satisfies Record<InvitationStatus, number>;

export const SentInvitationsTable = () => {
  const { data: user } = useUser();

  const { t } = useTranslation(['invitations']);

  const invitations = useMemo(
    () =>
      user.invitations
        .filter(
          (invitationA) =>
            !user.invitations.some(
              (invitationB) =>
                invitationA.email === invitationB.email &&
                invitationA.id !== invitationB.id &&
                (invitationA.status === invitationB.status
                  ? dayjs(invitationA.statusDate).isBefore(dayjs(invitationB.statusDate))
                  : invitationSortPriority[invitationA.status] > invitationSortPriority[invitationB.status]),
            ),
        )
        .sort(({ statusDate: a }, { statusDate: b }) => dayjs(b).diff(dayjs(a))),
    [user.invitations],
  );

  const columns = useSentInvitationsTableColumns();
  const [table, Table] = useTable({
    data: invitations,
    columns,
    initialState: {
      sorting: [{ id: ColumnIds.invitationsName, desc: false }],
    },
    manualPagination: true, // Show all rows
  });

  return (
    <div className={wrapperStyle}>
      <Title as="h3">{t('invitations:sentInvitations.title')}</Title>

      <Table.Root table={table}>
        <Table.Grid>
          <Table.Header />
          <Table.Body>{(row) => <Table.Row row={row} />}</Table.Body>
        </Table.Grid>
      </Table.Root>
    </div>
  );
};
